import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IncompleteCostResponse } from '../value.api';
import { formatEur } from '../format';

interface IncompleteCostBreakdownProps {
  data: IncompleteCostResponse;
}

const CATEGORY_ICONS: Record<string, string> = {
  LABEL: 'mdi-label-outline',
  PHOTO: 'mdi-camera-outline',
  MARKETING: 'mdi-bullhorn-outline',
  PACKINFO: 'mdi-package-variant',
  HAZARDOUS: 'mdi-alert-octagon-outline',
};

function RiskBadge({ level }: { level: 'high' | 'medium' | 'low' }) {
  const { t } = useTranslation();
  const styles: Record<string, { bg: string; color: string }> = {
    high: { bg: 'var(--destructive-light)', color: 'var(--destructive)' },
    medium: { bg: 'var(--warning-light, #FEF3E2)', color: 'var(--warning, #E67E22)' },
    low: { bg: 'var(--secondary-light)', color: 'var(--secondary)' },
  };
  const s = styles[level] ?? styles.medium!;
  const label = level === 'high' ? t('incompleteCost.riskHigh') : level === 'medium' ? t('incompleteCost.riskMedium') : t('incompleteCost.riskLow');

  return (
    <span
      className="rounded px-2 py-0.5 text-[0.6875rem] font-medium"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {label}
    </span>
  );
}

export function IncompleteCostBreakdown({ data }: IncompleteCostBreakdownProps) {
  const { t } = useTranslation();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  if (data.totalMissingFields === 0) return null;

  // Sort categories by estimatedCost desc, put non-applicable at end
  const sortedCategories = [...data.categories].sort((a, b) => {
    if (a.applicable !== b.applicable) return a.applicable ? -1 : 1;
    return b.estimatedCost - a.estimatedCost;
  });

  const productsForCategory = (categoryId: string) =>
    data.topProducts.filter(p => p.missingCategories.includes(categoryId));

  return (
    <div data-testid="incomplete-cost-breakdown" className="space-y-3">
      <div>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--foreground-heading)' }}>
          {t('incompleteCost.title')}
        </h2>
        <p className="mt-[3px]" style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}>
          {t('incompleteCost.subtitle')}
        </p>
      </div>

      {/* Per-category rows */}
      <div className="space-y-2">
        {sortedCategories.map(cat => {
          const isExpanded = expandedCategory === cat.categoryId;
          const icon = CATEGORY_ICONS[cat.categoryId] ?? 'mdi-help-circle-outline';
          const catProducts = productsForCategory(cat.categoryId);

          if (!cat.applicable) {
            return (
              <div
                key={cat.categoryId}
                className="rounded-xl px-4 py-3 opacity-50"
                style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}
              >
                <div className="flex items-center gap-2">
                  <span className={`mdi ${icon}`} style={{ fontSize: '18px', color: 'var(--foreground-muted)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--foreground-muted)' }}>
                    {t(`categories.${cat.categoryId}`)}
                  </span>
                  <span className="ml-auto text-xs" style={{ color: 'var(--foreground-muted)' }}>
                    {t('incompleteCost.notApplicable')}
                  </span>
                </div>
              </div>
            );
          }

          return (
            <div
              key={cat.categoryId}
              className="rounded-xl"
              style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow)' }}
            >
              <button
                type="button"
                onClick={() => setExpandedCategory(isExpanded ? null : cat.categoryId)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left"
              >
                <span className={`mdi ${icon}`} style={{ fontSize: '18px', color: 'var(--primary)' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                    {t(`categories.${cat.categoryId}`)}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                    {cat.missingFields} {t('incompleteCost.missingFieldsLabel')} {t('incompleteCost.affectedProducts', { count: cat.affectedProducts })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                    {formatEur(cat.estimatedCost)}
                  </span>
                  <RiskBadge level={cat.riskLevel} />
                  <span
                    className="mdi mdi-chevron-down transition-transform"
                    style={{ fontSize: '20px', color: 'var(--foreground-muted)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </div>
              </button>

              {isExpanded && catProducts.length > 0 && (
                <div className="border-t px-4 py-2" style={{ borderColor: 'var(--border-soft)' }}>
                  <div className="space-y-1.5">
                    {catProducts.map(product => (
                      <div key={product.id} className="flex items-center justify-between py-1 text-xs">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium" style={{ color: 'var(--foreground)' }}>{product.name}</p>
                          <p style={{ color: 'var(--foreground-muted)' }}>{product.gtin}</p>
                        </div>
                        <div className="text-right ml-3">
                          <p style={{ color: 'var(--foreground-muted)' }}>{product.missingFieldCount} {t('incompleteCost.fieldsShort')}</p>
                          <p className="font-medium" style={{ color: 'var(--foreground)' }}>{formatEur(product.estimatedCost)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* How calculated */}
      <div className="rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}>
        <button
          type="button"
          onClick={() => setShowExplanation(!showExplanation)}
          className="flex w-full items-center justify-between text-left"
        >
          <span className="text-xs font-medium" style={{ color: 'var(--primary)' }}>
            {t('incompleteCost.howCalculated')}
          </span>
          <span
            className="mdi mdi-chevron-down transition-transform"
            style={{ fontSize: '16px', color: 'var(--primary)', transform: showExplanation ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>
        {showExplanation && (
          <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--foreground-muted)' }}>
            {t('incompleteCost.calculationExplanation', { rate: data.parameters.hourlyRate })}
          </p>
        )}
      </div>
    </div>
  );
}
